import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { Star, X, Trash2, Pencil, MessageSquareReply } from "lucide-react";
import ReportButton from "@/components/ReportButton";

export function Stars({ value, size = 12, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-px ${className}`} aria-label={`${value} estrelas`}>
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} className={i <= Math.round(value) ? "fill-[#ffd100] text-[#ffd100]" : "text-zinc-300"} />)}
    </span>
  );
}

export function Rating({ avg, count, size = "sm", testid = "rating" }) {
  if (!count) return null;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 font-semibold ${size === "lg" ? "text-sm" : "text-[11px]"} text-zinc-800`}>
      <Star size={size === "lg" ? 14 : 11} className="fill-[#ffd100] text-[#ffd100]" /> {Number(avg).toFixed(1)} <span className="text-zinc-500 font-normal">({count})</span>
    </span>
  );
}

export function StarInput({ value, onChange, testid = "star-input" }) {
  return (
    <div data-testid={testid} className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" data-testid={`${testid}-${i}`} onClick={() => onChange(i)} className="p-1 transition-transform hover:scale-110">
          <Star size={28} className={i <= value ? "fill-[#ffd100] text-[#ffd100]" : "text-zinc-300"} />
        </button>
      ))}
    </div>
  );
}

const fmt = (iso) => new Date(iso).toLocaleDateString("pt-PT");

export function ReviewItem({ rv, children }) {
  return (
    <div data-testid={`review-${rv.id}`} className="bg-white rounded-2xl border border-zinc-200 p-3 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-sm">{rv.buyer_name}</div>
          <div className="flex items-center gap-2 mt-0.5"><Stars value={rv.rating} />
            <span data-testid={`review-label-${rv.id}`} className={`text-[10px] font-semibold px-1.5 py-px rounded-full ${rv.label_kind === "reservation" ? "bg-[#fff8d6] text-zinc-900" : "bg-green-50 text-green-700"}`}>{rv.verified_label}</span>
          </div>
        </div>
        <div className="text-[10px] text-zinc-400 text-right">{fmt(rv.created_at)}{rv.edited && " · editada"}</div>
      </div>
      {rv.target_name && <div className="text-[11px] text-zinc-500 mt-1">{rv.target_name}</div>}
      {rv.comment && <p className="mt-1.5 text-zinc-700 whitespace-pre-line leading-relaxed">{rv.comment}</p>}
      {rv.seller_reply && (
        <div data-testid={`review-reply-${rv.id}`} className="mt-2 p-2.5 rounded-xl bg-zinc-50 border border-zinc-200">
          <div className="font-semibold text-[11px] flex items-center gap-1"><MessageSquareReply size={11} /> Resposta da loja <span className="text-zinc-400 font-normal">· {fmt(rv.seller_reply.at)}</span></div>
          <p className="mt-1 text-zinc-700 whitespace-pre-line">{rv.seller_reply.text}</p>
        </div>
      )}
      {children}
    </div>
  );
}

export function ReviewsList({ target_type, target_id, seller_id, title = "Avaliações", testid = "reviews-list" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = target_type ? { target_type, target_id } : { seller_id };
    api.get("/reviews", { params }).then(r => setData(r.data)).catch(() => setData({ items: [], rating_count: 0 }));
  }, [target_type, target_id, seller_id]);
  if (!data) return null;
  return (
    <section data-testid={testid} className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display font-bold text-base">{title}</h2>
        <Rating avg={data.rating_avg} count={data.rating_count} size="lg" testid={`${testid}-rating`} />
      </div>
      {data.items.length === 0 && <p data-testid={`${testid}-empty`} className="text-sm text-zinc-500 py-4 text-center bg-white rounded-2xl border border-dashed border-zinc-200">Ainda sem avaliações.</p>}
      <div className="space-y-2">
        {data.items.map(rv => <ReviewItem key={rv.id} rv={rv}><div className="mt-2 flex justify-end"><ReportButton target_type="review" target_id={rv.id} /></div></ReviewItem>)}
      </div>
    </section>
  );
}

export function ReviewSheet({ exp, onClose, onDone }) {
  const { confirm } = useConfirm();
  const existing = exp.review;
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [busy, setBusy] = useState(false);
  const isRes = exp.label_kind === "reservation";

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Escolhe uma classificação de 1 a 5 estrelas");
    setBusy(true);
    try {
      if (existing) await api.patch(`/reviews/${existing.id}`, { rating, comment });
      else await api.post("/reviews", { source_type: exp.source_type, source_id: exp.source_id, target_type: exp.target_type, target_id: exp.target_id, rating, comment });
      toast.success(existing ? "Avaliação actualizada" : "Obrigado pela tua avaliação!");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro ao guardar"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!(await confirm({ title: "Apagar a tua avaliação?", description: "Não poderás voltar a avaliar esta experiência.", confirmLabel: "Apagar" }))) return;
    setBusy(true);
    try { await api.delete(`/reviews/${existing.id}`); toast.success("Avaliação apagada"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 pb-20 sm:pb-4" onClick={onClose}>
      <form data-testid="review-sheet" onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display font-extrabold text-lg">{existing ? "Editar avaliação" : "Avaliar"}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">{exp.target_name} · {exp.seller_name}</p>
            <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-px rounded-full ${isRes ? "bg-[#fff8d6]" : "bg-green-50 text-green-700"}`}>{isRes ? "Reserva concluída" : "Compra verificada"} · {exp.reference}</span>
          </div>
          <button type="button" onClick={onClose} data-testid="review-close" className="p-1 rounded-full hover:bg-zinc-100"><X size={18} /></button>
        </div>
        <div className="flex justify-center"><StarInput value={rating} onChange={setRating} /></div>
        <textarea data-testid="review-comment" value={comment} onChange={e => setComment(e.target.value.slice(0, 1500))} rows={4} placeholder="Conta como foi a tua experiência (opcional)"
                  className="w-full px-3 py-2 rounded-xl border border-zinc-200 bg-zinc-50 text-sm outline-none focus:border-black resize-none" />
        <div className="text-[10px] text-zinc-400 -mt-2">{comment.length}/1500{existing ? ` · podes editar até ${fmt(existing.edit_until)}` : ""}</div>
        <div className="flex gap-2 pt-1">
          {existing && <button type="button" data-testid="review-delete" onClick={remove} disabled={busy} className="px-3 py-2.5 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50"><Trash2 size={14} /></button>}
          <button type="submit" data-testid="review-submit" disabled={busy || !rating} className="flex-1 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white text-sm font-semibold transition-colors">{busy ? "A guardar…" : existing ? "Guardar alterações" : "Publicar avaliação"}</button>
        </div>
      </form>
    </div>
  );
}

export function useEligible(enabled = true) {
  const [items, setItems] = useState([]);
  const load = () => enabled && api.get("/reviews/eligible").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [enabled]);
  return { items, reload: load };
}

export function ReviewAction({ exp, onOpen }) {
  const rv = exp.review;
  if (rv?.status === "deleted") return <span data-testid={`review-deleted-${exp.source_id}`} className="text-[11px] text-zinc-400">Avaliação apagada</span>;
  if (rv) return (
    <div className="flex items-center gap-2 flex-wrap">
      <Stars value={rv.rating} /><span className="text-[11px] text-zinc-500">A tua avaliação{rv.status === "hidden" ? " (oculta pela moderação)" : ""}</span>
      {rv.can_edit && <button data-testid={`review-edit-${exp.source_id}-${exp.target_id}`} onClick={() => onOpen(exp)} className="text-[11px] font-semibold underline inline-flex items-center gap-1"><Pencil size={10} /> Editar</button>}
    </div>
  );
  if (exp.expired) return <span className="text-[11px] text-zinc-400">Prazo de avaliação terminado</span>;
  return <button data-testid={`review-btn-${exp.source_id}-${exp.target_id}`} onClick={() => onOpen(exp)} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#fff8d6] border border-[#ffd100] hover:bg-[#ffd100] transition-colors"><Star size={11} /> Avaliar {exp.target_type === "reservation" ? "reserva" : ""}</button>;
}
